import React, { useState, useEffect } from 'react';
import CollaborativeGrid from './CollaborativeGrid';
import { API_URL } from './constants';
import { toast } from 'react-toastify';

const CollaborativeEditor = ({ data, columnDefs }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [suggestedEdits, setSuggestedEdits] = useState([]);
  const [selectedEdit, setSelectedEdit] = useState(null);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [reviewComment, setReviewComment] = useState('');

  const fetchSuggestedEdits = async () => {
    try {
      const response = await fetch(`${API_URL}/api/get-suggested-edits`);
      if (!response.ok) {
        throw new Error('Failed to fetch suggested edits');
      }
      const edits = await response.json();
      setSuggestedEdits(edits);
    } catch (error) {
      console.error('Error fetching suggested edits:', error);
      toast.error('Failed to fetch suggested edits');
    }
  };

  useEffect(() => {
    fetchSuggestedEdits();
    const interval = setInterval(fetchSuggestedEdits, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleTabChange = (index) => {
    setActiveTab(index);
  };

  const handleEditSelect = (edit) => {
    setSelectedEdit(edit);
    setOpenReviewDialog(true);
  };

  const handleApproveEdit = async () => {
    if (!selectedEdit || !reviewComment.trim()) {
      toast.error('Please provide a review comment');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/approve-edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          editId: selectedEdit.id,
          reviewComment,
          reviewerId: localStorage.getItem('userId'),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to approve edit');
      }

      setSuggestedEdits(prev => 
        prev.map(edit => 
          edit.id === selectedEdit.id
            ? { ...edit, status: 'approved', reviewComment }
            : edit
        )
      );

      toast.success('Edit approved successfully');
      setOpenReviewDialog(false);
      setReviewComment('');
      setSelectedEdit(null);
    } catch (error) {
      console.error('Error approving edit:', error);
      toast.error('Failed to approve edit');
    }
  };

  const handleRejectEdit = async () => {
    if (!selectedEdit || !reviewComment.trim()) {
      toast.error('Please provide a review comment');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/reject-edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          editId: selectedEdit.id,
          reviewComment,
          reviewerId: localStorage.getItem('userId'),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to reject edit');
      }

      setSuggestedEdits(prev => 
        prev.map(edit => 
          edit.id === selectedEdit.id
            ? { ...edit, status: 'rejected', reviewComment }
            : edit
        )
      );

      toast.success('Edit rejected successfully');
      setOpenReviewDialog(false);
      setReviewComment('');
      setSelectedEdit(null);
    } catch (error) {
      console.error('Error rejecting edit:', error);
      toast.error('Failed to reject edit');
    }
  };

  const renderEditList = () => {
    const filteredEdits = suggestedEdits.filter(edit => 
      activeTab === 0 ? edit.status === 'pending' : 
      activeTab === 1 ? edit.status === 'approved' : 
      edit.status === 'rejected'
    );

    return (
      <div className="mt-4">
        {filteredEdits.map((edit) => (
          <div
            key={edit.id}
            className="p-4 mb-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
            onClick={() => handleEditSelect(edit)}
          >
            <div className="font-medium">
              {edit.column}: {edit.oldValue} → {edit.newValue}
            </div>
            <div className="text-sm text-gray-500">
              Suggested by: {edit.userId} | {new Date(edit.timestamp).toLocaleString()}
            </div>
            {edit.comment && (
              <div className="mt-2 text-sm">
                Comment: {edit.comment}
              </div>
            )}
          </div>
        ))}
        {filteredEdits.length === 0 && (
          <div className="text-center text-gray-500 mt-4">
            No {activeTab === 0 ? 'pending' : activeTab === 1 ? 'approved' : 'rejected'} edits
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="border-b border-gray-200">
        <div className="flex">
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === 0
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => handleTabChange(0)}
          >
            Pending Edits
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === 1
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => handleTabChange(1)}
          >
            Approved Edits
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === 2
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => handleTabChange(2)}
          >
            Rejected Edits
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <CollaborativeGrid
            initialData={data}
            columnDefs={columnDefs}
            onDataUpdate={fetchSuggestedEdits}
          />
        </div>
        <div className="w-80 p-4 border-l border-gray-200">
          {renderEditList()}
        </div>
      </div>

      {openReviewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Review Edit</h3>
            <div className="space-y-2 mb-4">
              <p><span className="font-medium">Column:</span> {selectedEdit?.column}</p>
              <p><span className="font-medium">Change:</span> {selectedEdit?.oldValue} → {selectedEdit?.newValue}</p>
              <p><span className="font-medium">Suggested by:</span> {selectedEdit?.userId}</p>
              <p><span className="font-medium">Comment:</span> {selectedEdit?.comment}</p>
            </div>
            <textarea
              className="w-full p-2 border rounded-md mb-4"
              rows={4}
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              placeholder="Add your review comment..."
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setOpenReviewDialog(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
              >
                Cancel
              </button>
              {activeTab === 0 && (
                <>
                  <button
                    onClick={handleRejectEdit}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Reject
                  </button>
                  <button
                    onClick={handleApproveEdit}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Approve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollaborativeEditor; 